<script setup>
import { computed, defineCustomElement, onBeforeMount } from 'vue';
import { Head, usePage } from '@inertiajs/vue3';
import { TagIcon } from '@heroicons/vue/24/outline';
import { ArrowUpIcon } from '@heroicons/vue/20/solid';
import { kebabCase } from 'change-case';
import { useTitle } from '@/Composables/title.js';
import { useReadingTimeInMinutes } from '@/Composables/readingTime.js';
import { useGetLocalizedDateTime } from '@/Composables/localizedDateTime.js';
import { toCamelCase, filterContent } from '@/Composables/statamicModules.js';
import { useLinkify } from '@/Composables/linkify.js';
import Breadcrumb from '@/Components/Breadcrumb.vue';
import StatamicBaseComponent from '@/Components/StatamicBaseComponent.vue';
import StatamicText from '@/Components/Statamic/StatamicText.vue';
import RelatedContent from '@/Pages/CorporatePublishing/Partials/RelatedContent.vue';

// Import custom elements
const modules = import.meta.glob('../../../CustomElements/**.ce.vue');

onBeforeMount(async () => {
    for (const path in modules) {
        if (Object.hasOwn(modules, path)) {
            modules[path]().then(module_ => {
                const name = kebabCase(path.replace(/^.*[\\/]/, '').replace('.ce.vue', ''));

                if (!customElements.get(name)) {
                    customElements.define(name, defineCustomElement(module_.default));
                }
            });
        }
    }
});

/* eslint-disable camelcase */
/* eslint-disable vue/prop-name-casing */
const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    dpa_article: {
        type: Object,
        required: true,
        default() {},
    },
    before_content: {
        type: Array,
        required: false,
        default: () => [],
    },
    after_content: {
        type: Array,
        required: false,
        default: () => [],
    },
    footnotes: {
        type: Array,
        default: null,
        required: false,
    },
    primary_event_topics: {
        type: Object,
        required: false,
        default() {},
    },
    secondary_event_topics: {
        type: Object,
        required: false,
        default() {},
    },
    primary_event_business_areas: {
        type: Object,
        required: false,
        default() {},
    },
    secondary_event_business_areas: {
        type: Object,
        required: false,
        default() {},
    },
    image: {
        type: Object,
        required: false,
        default: null,
    },
    show_reading_time: {
        type: Boolean,
        default: false,
    },
    type: {
        type: String,
        required: false,
        default: null,
    },
    relatedContent: {
        type: Object,
        required: false,
        default: null,
    },
});
/* eslint-enable camelcase */
/* eslint-enable vue/prop-name-casing */

const page = usePage();
const longLocale = computed(() => page.props.longLocale);
const currentUrl = computed(() => page.props.currentUrl);
const appName = computed(() => page.props.appName);
const eventBusinessAreas = computed(() => props.primary_event_business_areas.concat(props.secondary_event_business_areas));
const eventTopics = computed(() => props.primary_event_topics.concat(props.secondary_event_topics));
const readingTime = computed(() => useReadingTimeInMinutes(props.dpa_article.content ?? ''));
</script>

<template>
    <Head>
        <title>{{ useTitle(title) }}</title>
        <meta
            name="description"
            :content="dpa_article.teaser"
        >
        <meta
            property="og:locale"
            :content="longLocale"
        >
        <meta
            property="og:url"
            :content="currentUrl"
        >
        <meta
            property="og:site_name"
            :content="appName"
        >
        <meta
            property="og:type"
            content="website"
        >
        <meta
            property="og:title"
            :content="title"
        >
        <meta
            property="og:description"
            :content="dpa_article.teaser"
        >
        <meta
            property="og:image"
            content=""
        >
    </Head>

    <div class="relative bg-white">
        <div class="container px-4 md:py-4 mx-auto">
            <Breadcrumb class="mt-4" />
        </div>

        <div class="max-w-4xl px-4 pt-8 pb-16 mx-auto text-gray-800">
            <!-- meta -->
            <div class="space-y-8">
                <div
                    class="flex text-xs md:text-sm text-gray-500 py-3 px-2 gap-x-12 border-t border-b border-gray-200"
                >
                    <div>
                        {{ $t('articles.show.publishedAt') }} <span class="text-gray-700">{{ useGetLocalizedDateTime(dpa_article.published_at) }}</span>
                    </div>

                    <div v-if="show_reading_time">
                        {{ $t('articles.show.readingTime') }}
                        <span class="text-gray-700">{{ readingTime }} {{ $t('articles.show.minutesShort') }}</span>
                    </div>
                </div>

                <div class="space-y-5">
                    <div class="flex flex-col sm:flex-row justify-between gap-y-4 gap-x-2">
                        <div class="flex flex-wrap gap-x-4 gap-y-2">
                            <div
                                v-if="eventBusinessAreas.length > 0"
                                class="flex text-deep-teal-700 space-x-2"
                            >
                                <span
                                    v-for="eventBusinessArea in eventBusinessAreas.filter(eventBusinessArea => eventBusinessArea.icon)"
                                    :key="eventBusinessArea.id"
                                    :title="eventBusinessArea.name"
                                    class="size-7 inline-block bg-deep-teal-100 rounded-full p-1"
                                    v-html="eventBusinessArea.icon"
                                />
                            </div>

                            <div v-if="type">
                                <span
                                    class="font-bold text-xs/tight text-deep-teal-700 bg-deep-teal-100 rounded-full py-1 px-2"
                                >
                                    {{ $t(`enums.collectionType.${type}`) }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="eventTopics.length > 0"
                        class="flex items-start space-x-1.5"
                    >
                        <TagIcon
                            class="shrink-0 size-5 text-gray-500 mt-px md:mt-0.5"
                            aria-hidden="true"
                        />

                        <div class="text-sm md:text-base">
                            {{ eventTopics.map((topic) => topic.title).join(', ') }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- content -->
            <div class="mt-12 space-y-4 [counter-reset:footnote]">
                <h1 class="font-semibold text-2xl md:text-4xl text-deep-teal-700">
                    {{ title }}
                </h1>

                <div
                    v-if="dpa_article.teaser"
                    class="font-semibold"
                >
                    {{ dpa_article.teaser }}
                </div>

                <div
                    v-if="image && image?.avif"
                    class="py-4"
                >
                    <figure class="space-y-2">
                        <picture>
                            <source
                                :srcset="image.avif"
                                type="image/avif"
                            >
                            <source
                                :srcset="image.webp"
                                type="image/webp"
                            >
                            <img
                                class="h-full w-full object-center rounded-xl"
                                :src="image.jpg"
                                :alt="image.alt"
                                loading="lazy"
                            >
                        </picture>

                        <figcaption
                            v-if="image.creditLine"
                            class="text-sm text-gray-500 md:px-4"
                        >
                            {{ $t('components.creditLine.prefix') }} {{ image.creditLine }}
                        </figcaption>
                    </figure>
                </div>

                <div
                    class="space-y-8 md:space-y-10 lg:space-y-12 [&_.container]:max-w-none [&_.container]:m-0 [&_.container]:p-0"
                >
                    <StatamicBaseComponent
                        v-for="(module, index) in before_content"
                        :key="index"
                        :name="`Statamic${toCamelCase(module.type)}`"
                        :parameters="filterContent(module)"
                    />

                    <StatamicText
                        v-if="dpa_article.content"
                        :text="dpa_article.content"
                    />

                    <StatamicBaseComponent
                        v-for="(module, index) in after_content"
                        :key="index"
                        :name="`Statamic${toCamelCase(module.type)}`"
                        :parameters="filterContent(module)"
                    />

                    <div
                        v-if="dpa_article.autopublishNotice"
                        class="text-sm text-gray-500"
                    >
                        {{ dpa_article.autopublishNotice }}
                    </div>
                </div>

                <div v-if="footnotes && footnotes.length > 0">
                    <div class="mt-12">
                        <ol class="border-t border-t-gray-100 px-2 pt-2 space-y-1 [counter-reset:footnoteList]">
                            <li
                                v-for="footnote in footnotes"
                                :id="`fn-list-${footnote.id}`"
                                :key="footnote.id"
                                class="[counter-increment:footnoteList] before:content-[counter(footnoteList)] before:font-bold before:text-[10px] before:align-super before:pr-3"
                            >
                                <span
                                    class="prose max-w-none leading-relaxed"
                                    v-html="useLinkify(footnote.text)"
                                />

                                <a
                                    :href="`#fn-${footnote.id}`"
                                    class="inline-block ml-1 mb-[3px]"
                                >
                                    <ArrowUpIcon class="size-3" />
                                </a>
                            </li>
                        </ol>
                    </div>
                </div>
            </div>

            <RelatedContent
                :related-content
                :site-name="page.props.siteName"
                :locale="page.props.locale"
            />
        </div>
    </div>
</template>
